// 剣舞の新しい技「暗器投げ」(2026-10-25・開発版 ?bladeThrow=1)。アルレッキーノ (Lies of P) の、離れると輪の刃を投げて床を転がす技を落とし込んだ。
//   作者「剣舞の技の数は全然増やしていいよ。ひとまずアルレッキーノを剣舞に落とし込む形で作ってもらって、実プレイしながら調整していきたい。」
//   技の組み立て = docs/research/2026-10-25-arlecchino-attack-analysis.md。輪の刃の代わりに、袖に隠した小刀を2本、床へ転がすように投げる。
//   流れ: 腰を落として小刀を握った手を引く (手もとが光る = 投げる合図) → 前へ放つ (1本目) → もう一度引いて → 放つ (2本目・約 0.52 秒後) → 立ち姿。
//   小刀は放った高さのまま刃先を前へ向けて一直線に飛ぶ (10-27 作者の答え・描く所 = src/blade_dancer_throw_knife.ts)。当たりは横の位置だけ (飛ぶ弾の決まり)。
//   2本とも弾ける。ちょうどで弾くと剣舞の胸へ跳ね返る (飛ぶ弾の共通の決まり)。離れている時だけ選ばれる (近いと出ない)。
//   絵 = 引き寄せと同じ m1025_c_{2,3} を流用している (仮)。投げ専用の絵が要るかは遊んでから決める。
import { ATTACKS } from './attacks'
import { PLAYER_DAMAGE } from './balance'
import { BOSS_DEFINITIONS } from './bosses'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import type { AttackDef } from './types'
import { TENSE, withDissolves } from './blade_dancer_art'
import { BLADE_THROW_ID, THROW_HAND_RELEASE } from './blade_dancer_throw_knife'

export { BLADE_THROW_ID } from './blade_dancer_throw_knife'
const BOSS_ID = 'blade-dancer'
const C = (n: number) => `m1025_c_${n}`
const SETTLE = 'm1020_waltz_s2'
const STAND = 'm1020_waltz_c6'

const TICK = 16
const ACTIVE_MS = 130
const SECOND_AFTER_MS = 520          // 1本目から2本目まで
export const BLADE_THROW_GAPS_MS = [SECOND_AFTER_MS - ACTIVE_MS - TICK]
const CHARGE_MS = 1000
const RECOVERY_MS = 800

export const BLADE_THROW_SPEED = 820   // px/秒 (10-27 鋭く一直線に飛ぶ刃にした時に 560→820。画面の端から端まで 1.2 秒ほど)
export const BLADE_THROW_WIDTH = 80   // 横向きに飛ぶ刃 (長さ 84px) の芯。当たりを絵に合わせる (10-27 転がる輪→一直線の刃で 64→80)
/** これより近いと選ばれない (近い時は斬る技を出す)。 */
export const BLADE_THROW_FAR_ONLY_PX = 280
const APPROACH_PX = 400

export const BLADE_THROW_TIMELINE: BossAttackTimeline = withDissolves([
  { phase: 'telegraph', at: 0, image: SETTLE, hold: true, tag: 'anticipation', soft: true },
  { phase: 'telegraph', at: 0.12, image: C(3), hold: true, ease: 'easeIn' },                 // 小刀を握った手を腰へ引く
  { phase: 'telegraph', at: 0.65, image: C(3), hold: true, ...TENSE },                      // 手もとが光る
  { phase: 'active', at: 0, image: C(2), hold: true, tag: 'contact', hitIndex: 0 },         // 1本目を放つ
  { phase: 'comboGap', at: 0, image: C(2), hold: true, tag: 'followThrough', hitIndex: 0 },
  { phase: 'comboGap', at: 0.3, image: C(3), hold: true, hitIndex: 0 },                      // もう一度引く
  { phase: 'comboGap', at: 0.75, image: C(3), hold: true, hitIndex: 0, ...TENSE },
  { phase: 'active', at: 0, image: C(2), hold: true, tag: 'contact', hitIndex: 1 },         // 2本目を放つ
  { phase: 'recovery', at: 0, image: C(2), hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.4, image: STAND, hold: true, tag: 'recover', ease: 'easeOut', soft: true },
  { phase: 'recovery', at: 0.8, image: 'idle', hold: true },
], { telegraph: CHARGE_MS, recovery: RECOVERY_MS })

const DEF: AttackDef = {
  id: BLADE_THROW_ID,
  label: '暗器投げ',
  kind: 'projectile',
  chargeMs: CHARGE_MS, activeMs: ACTIVE_MS, recoveryMs: RECOVERY_MS,
  parryable: true, defense: 'parry',
  damage: PLAYER_DAMAGE.standard, postureDamage: 16,
  comboHits: 2, comboGapMs: BLADE_THROW_GAPS_MS[0], comboGapsMs: [...BLADE_THROW_GAPS_MS],
  range: 0, approachDistancePx: APPROACH_PX, lungeDistance: 0,
  farOnlyPx: BLADE_THROW_FAR_ONLY_PX,
  // 構えの間は相手へ向き直る (離れた相手を狙って投げる)。放つ少し前で向きを決める。
  turnsDuringTelegraph: true, facingLockBeforeActiveMs: 120,
  crossingPolicy: 'not-applicable', crossingPolicyExemption: 'travel-projectile',
  castProjectile: {
    speedPxPerSec: BLADE_THROW_SPEED, widthPx: BLADE_THROW_WIDTH,
    spawnForwardPx: THROW_HAND_RELEASE.fwd, spawnOffsetY: THROW_HAND_RELEASE.up,
  },
  sourceBossId: BOSS_ID,
}

let installed = false

/** 剣舞に「暗器投げ」を足す (DEV ?bladeThrow=1)。最初から出る技の組に入れる。 */
export function useBladeDancerThrow(): void {
  if (!installed) {
    ATTACKS[BLADE_THROW_ID] = { ...DEF, castProjectile: { ...DEF.castProjectile! } }
    const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
    def.baseAttackIds.push(BLADE_THROW_ID)
    def.unlockByExecutes?.[0]?.ids.push(BLADE_THROW_ID)
    installed = true
  }
  BOSS_ATTACK_TIMELINES[BOSS_ID] = { ...(BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}), [BLADE_THROW_ID]: BLADE_THROW_TIMELINE }
}

/** 外す (テストの後始末用)。 */
export function restoreBladeDancerThrow(): void {
  if (!installed) return
  delete ATTACKS[BLADE_THROW_ID]
  const def = BOSS_DEFINITIONS.find(b => b.id === BOSS_ID)!
  def.baseAttackIds = def.baseAttackIds.filter(id => id !== BLADE_THROW_ID)
  const first = def.unlockByExecutes?.[0]
  if (first) first.ids = first.ids.filter(id => id !== BLADE_THROW_ID)
  delete BOSS_ATTACK_TIMELINES[BOSS_ID]?.[BLADE_THROW_ID]
  installed = false
}

export function parseBladeThrow(v: string | null): boolean {
  return v === '1'
}
